/** @jsx jsx */
import { connect } from 'react-redux';

import { LegacyButton, Spacer, Text, Typography } from '@zapier/design-system';
import { css, jsx } from '@emotion/core';

import { State } from 'app/common/types';
import { WistiaAppVideo } from 'app/developer-v3/components/WistiaAppVideo';
import {
  createPartnerUserInteractionEventTracker,
  PartnerUserInteractionEventTracker,
} from 'app/developer-v3/teams/team-partner-sharing/createInteractionTracker';
import { ZAP_EDITOR_URL } from 'app/common/constants';

type Props = {
  trackInteraction: PartnerUserInteractionEventTracker;
};

const stylePage = css({
  display: 'flex',
  flexDirection: 'column',
  padding: '25px 45px',

  '& b': {
    fontWeight: 700,
  },

  '& p': {
    ...Typography.Body1,
  },
});

const styleButtonWrapper = css`
  max-width: 258px;
`;

const styleImage = css`
  position: relative;
  align-self: center;
  height: 65%;
  width: 65%;
`;

function EmbedZapEditor({ trackInteraction }: Props): JSX.Element {
  return (
    <div css={stylePage}>
      <Text margin="0 0 25px" tag="h1" type="PageHeader">
        Zap Editor
      </Text>
      <p>
        By embedding our Zap Editor, your users can create and edit their Zaps
        without leaving your product. This will offer them the best user
        experience as they build workflows with your integration.
      </p>
      <p>
        <b>Integrations that embedded the Zap Editor:</b>{' '}
        <WistiaAppVideo
          defaultName="JotForm"
          popoverCaption="JotForm using the Zap Editor"
          slug="jotform"
          videoId="wj5gmwmc6z"
        />{' '}
        <WistiaAppVideo
          defaultName="Swipe Pages"
          popoverCaption="Swipe Pages using the Zap Editor"
          slug="swipe-pages"
          videoId="0jarh919p1"
        />
      </p>
      <h2>Creating a new Zap</h2>
      <p>
        Use the Partner API to query the public Zap templates featuring your
        integration and feature them in your product. When a user chooses a Zap
        template they&apos;d like to try, load the Zap Editor in an embedded
        frame so your users can create the Zap without leaving your product.
      </p>
      <h2>Editing a Zap</h2>
      <p>
        Use the Partner API to load a user&apos;s Zaps. When the user chooses to
        open or edit a Zap, load the Zap Editor in an embedded frame so your
        users can edit the Zap inside your product.
      </p>
      <h2>Zap prefill options</h2>
      <p>
        Prefills allow you to define the fields on behalf of the user, thus
        simplying the experience of setting up their Zap. When creating the Zap,
        add parameters so that the user&apos;s Zap is prefilled with the
        provided custom values.
      </p>
      <Spacer height="30px" />
      <div css={styleButtonWrapper}>
        <LegacyButton
          href={ZAP_EDITOR_URL}
          isFullWidth={false}
          onClick={() => {
            trackInteraction({
              eventAction: 'click',
              interactionName: 'embed_zap_editor',
              interactionGoal: 'Read documentation to embed Zap Editor',
            });
          }}
          target="_blank"
        >
          Embed Zap Editor
        </LegacyButton>
      </div>
      <Spacer height="15px" />
      <h2>Example of your Zap Editor (if your integration has a trigger)</h2>
      <img
        alt="Zap Editor"
        css={styleImage}
        height={537}
        src="/images/zap-editor.png"
        width={670}
      />
    </div>
  );
}

function mapStateToProps(state: State): Partial<Props> {
  const trackInteraction = createPartnerUserInteractionEventTracker(state);
  return { trackInteraction };
}

export default connect(mapStateToProps)(EmbedZapEditor);
